import { MouseEventHandler } from 'react';
import { useNavigate } from 'react-router-dom';

import { Banner, Button, Flex } from '@spotify-internal/encore-web';

import { getErrorMessage } from '../../utils';
import { AnimatedLoginTitle } from './AnimatedLoginTitle';
import { AuthScreen } from './AuthScreen';
import { LoginErrorMessage } from './LoginErrorMessage';
import { NoWorkspaceState } from './NoWorkspaceState';

import styles from './LoginPage.module.css';

const NO_ORGANIZATION_MEMBERSHIP = 'does not belong to any organization';

// eslint-disable-next-line @spotify-confidence/page-requires-tracking -- Pre-auth page in core-react cannot depend on tracking-react
export const LoginPage = ({
  onLogin,
  onUseDifferentAccount,
  loading,
  error,
}: {
  onLogin?: MouseEventHandler<HTMLButtonElement>;
  onUseDifferentAccount?: MouseEventHandler<HTMLButtonElement>;
  loading?: boolean;
  error?: Error;
}) => {
  const navigate = useNavigate();
  const needsWorkspace = getErrorMessage(error, '').includes(
    NO_ORGANIZATION_MEMBERSHIP,
  );

  return (
    <AuthScreen.Root data-testid="login-page-root">
      <AuthScreen.Main>
        <Flex direction="column" gap="looser-3" alignItems="center">
          <AuthScreen.Logo color="currentColor" />
          {needsWorkspace ? (
            <NoWorkspaceState
              onCreateWorkspace={() => navigate('/signup')}
              onUseDifferentAccount={onUseDifferentAccount}
              loading={loading}
            />
          ) : (
            <>
              <AnimatedLoginTitle />
              <div className={styles.container}>
                <AuthScreen.Actions>
                  <Button
                    variant="primary"
                    colorSet="inverted"
                    size="large"
                    fullWidth
                    data-testid="login-button"
                    onClick={onLogin}
                    disabled={loading}
                    isLoading={loading}
                    loadingAriaLabel="Authenticating"
                  >
                    Log in
                  </Button>
                  <Button
                    variant="secondary-bordered"
                    semanticColor="textBase"
                    size="large"
                    fullWidth
                    data-testid="signup-button"
                    onClick={() => navigate('/signup')}
                    disabled={loading}
                  >
                    Sign up
                  </Button>
                </AuthScreen.Actions>
                {error && (
                  <Banner colorSet="negative" contextual role="alert">
                    <LoginErrorMessage error={error} />
                  </Banner>
                )}
              </div>
            </>
          )}
        </Flex>
      </AuthScreen.Main>
    </AuthScreen.Root>
  );
};
